import Vue from "vue";
import VueRouter from "vue-router";
import AccountView from "@/views/Account.vue";
import HomeView from "@/views/Home.vue";
import MenuView from "@/views/Menu.vue";
import SearchView from "@/views/Search.vue";
import { getStoreName } from "@/util.js";
import store from "@/store/index.js";
import vuetify from "@/plugins/vuetify";

Vue.use(VueRouter);

const navigationGuards = {
  checkIfLoyaltyIncluded: (to, from, next) => {
    if (
      (to.hash.toLowerCase() === "#rewards" || to.name === "rewards") &&
      process.env.VUE_APP_INCLUDE_LOYALTY.toLowerCase() !== "true"
    ) {
      next({ name: "home" });
    }
  },
  doNotRouteToRequestPasswordResetViewIfAuthenticatedAndComingFromLogin: (
    to,
    from,
    next,
  ) => {
    if (store.getters["auth/isAuthenticated"] && from.name === "login") {
      next({ name: "home" });
    }
    next();
  },
  openLoginAndRouteToOrderHistory: (toHash, toOrderId) => {
    if (toHash !== "#orders") {
      return;
    }

    store.commit("orders/ROUTE_TO_ORDER_HISTORY", toOrderId);
    store.commit("auth/OPEN", true);
  },
  preventRouteToCheckoutIfCartIsEmpty: (to, from, next) => {
    if (!store.state.cart.products?.length) {
      next({ name: "home" });
    }
    next();
  },
  resetSearchResults: (to, from) => {
    if (!store.state.search.products.length) {
      return;
    }

    // const noResetRoutes = ["addFromCart", "cart", "details", "modifiers"];
    const noResetRoutes = ["cart", "product"];

    const inTo = noResetRoutes.includes(to.name);
    const inFrom = noResetRoutes.includes(from.name);

    !inTo && !inFrom && store.commit("search/RESET");
  },
};
const appRoutes = [
  {
    name: "about",
    component: () => import(/* webpackChunkName: "about" */ "@/views/About"),
    meta: { scrollToTop: true },
    path: "/about",
  },
  {
    name: "account",
    component: AccountView,
    meta: {
      requiresAuth: true,
      scrollToTop: true,
    },
    path: "/account/:orderId?",
  },
  {
    component: () =>
      import(/* webpackChunkName: "login" */ "@/views/Auth/LogIn"),
    meta: { scrollToTop: true },
    path: "/login",
    children: [
      {
        name: "email",
        component: () =>
          import(
            /* webpackChunkName: "login-email" */ "@/components/Auth/LogIn/Content/Email"
          ),
        meta: { scrollToTop: true },
        path: "email",
      },
      {
        name: "login",
        component: () =>
          import(
            /* webpackChunkName: "login-options" */ "@/components/Auth/LogIn/Content/Options"
          ),
        meta: { scrollToTop: true },
        path: "",
      },
      {
        name: "phone",
        component: () =>
          import(
            /* webpackChunkName: "login-phone" */ "@/components/Auth/LogIn/Content/Phone"
          ),
        meta: { scrollToTop: true },
        path: "phone",
      },
      {
        name: "sms",
        component: () =>
          import(
            /* webpackChunkName: "login-sms" */ "@/components/Auth/LogIn/Content/SMS"
          ),
        meta: { scrollToTop: true },
        path: "sms",
      },
    ],
  },
  {
    name: "request",
    beforeEnter:
      navigationGuards.doNotRouteToRequestPasswordResetViewIfAuthenticatedAndComingFromLogin,
    component: () =>
      import(/* webpackChunkName: "request" */ "@/views/Auth/Request"),
    meta: { scrollToTop: true },
    path: "/request",
  },
  {
    name: "resetPassword",
    component: () =>
      import(/* webpackChunkName: "reset" */ "@/views/Auth/Reset"),
    meta: { scrollToTop: true },
    path: "/reset-password",
  },
  {
    name: "rewards",
    component: () =>
      import(/* webpackChunkName: "rewards" */ "@/views/Rewards/index"),
    meta: {
      requiresAuth: true,
      scrollToTop: true,
    },
    path: "/rewards",
  },
  {
    name: "rewardsProducts",
    component: () =>
      import(
        /* webpackChunkName: "rewards" */ "@/views/Rewards/Xs/RewardProducts"
      ),
    meta: {
      requiresAuth: true,
      scrollToTop: true,
    },
    props: (route) => ({
      ...route.params,
    }),
    path: "/rewards-products",
  },
  {
    name: "cart",
    component: () => import(/* webpackChunkName: "cart" */ "@/views/Cart"),
    meta: {
      requiresAuth: true,
      scrollToTop: true,
    },
    path: "/cart",
  },
  {
    name: "checkout",
    beforeEnter: navigationGuards.preventRouteToCheckoutIfCartIsEmpty,
    component: () =>
      import(/* webpackChunkName: "checkout" */ "@/views/Checkout"),
    meta: {
      requiresAuth: true,
      scrollToTop: true,
    },
    path: "/checkout",
  },
  {
    name: "signup",
    component: () =>
      import(/* webpackChunkName: "signUp" */ "@/views/Auth/SignUp"),
    meta: { scrollToTop: true },
    path: "/signup",
    children: [
      {
        name: "registration",
        component: () =>
          import(
            /* webpackChunkName: "signup-registration" */ "@/components/Auth/SignUp/Content/Registration"
          ),
        meta: { scrollToTop: true },
        path: "register",
        beforeEnter: (to, from, next) => {
          if (from.path === "/signup/confirm" || from.path === "/login/sms") {
            next();
          } else {
            next({ name: "identification" });
          }
        },
      },
      {
        name: "identification",
        component: () =>
          import(
            /* webpackChunkName: "signup-identification" */ "@/components/Auth/SignUp/Content/Identification"
          ),
        meta: { scrollToTop: true },
        path: "identify",
      },
      {
        name: "confirmation",
        component: () =>
          import(
            /* webpackChunkName: "signup-confirmation" */ "@/components/Auth/SignUp/Content/Confirmation"
          ),
        meta: { scrollToTop: true },
        path: "confirm",
        beforeEnter: (to, from, next) => {
          if (from.name === "identification" || from.name === "registration") {
            next();
          } else {
            next({ name: "identification" });
          }
        },
      },
    ],
  },
  {
    name: "deals",
    component: () =>
      import(/* webpackChunkName: "promotions" */ "@/views/Promotions"),
    meta: { scrollToTop: true },
    path: "/deals",
    children: [
      {
        name: "promotion",
        meta: { scrollToTop: true },
        component: () =>
          import(
            /* webpackChunkName: "promotion" */ "@/views/Promotions/Promotion"
          ),
        path: ":promotionDepartmentId",
      },
    ],
  },
  {
    name: "departments",
    component: () =>
      import(/* webpackChunkName: "departments" */ "@/views/Departments"),
    meta: { scrollToTop: true },
    path: "/departments",
  },
  {
    name: "department",
    component: () =>
      import(/* webpackChunkName: "department" */ "@/views/Department"),
    meta: { scrollToTop: true },
    path: "/department/:departmentId",
    children: [
      {
        name: "category",
        component: () =>
          import(
            /* webpackChunkName: "category" */ "@/views/Department/Category"
          ),
        meta: { scrollToTop: true },
        path: "category/:categoryId",
      },
    ],
  },
  {
    name: "favorites",
    component: () =>
      import(/* webpackChunkName: "favorites" */ "@/views/Favorites"),
    meta: { scrollToTop: true },
    path: "/favorites",
  },
  {
    name: "home",
    component: HomeView,
    meta: { scrollToTop: true },
    path: "/",
  },
  {
    component: MenuView,
    // meta: { scrollToTop: true },
    name: "menu",
    path: "/menu",
  },
  {
    component: () =>
      import(/* webpackChunkName: "popular" */ "@/views/Popular"),
    meta: { scrollToTop: true },
    name: "popular",
    path: "/popular",
  },
  {
    name: "product",
    component: () =>
      import(/* webpackChunkName: "product" */ "@/views/Product"),
    meta: { scrollToTop: true },
    path: "/product/:upc",
  },
  {
    name: "rewardProduct",
    component: () =>
      import(/* webpackChunkName: "product" */ "@/views/Product"),
    meta: { scrollToTop: true },
    path: "/reward-product/:upc?",
  },
  {
    name: "reset",
    component: HomeView,
    meta: { scrollToTop: true },
    path: "/reset/password/:token",
  },
  {
    name: "search",
    path: "/search",
    component: SearchView,
  },
  {
    name: "confirmed",
    component: () =>
      import(/* webpackChunkName: "confirmed" */ "@/views/Payment/Confirmed"),
    meta: {
      requiresAuth: true,
      scrollToTop: true,
    },
    path: "/confirmed/:orderId",
    props: true,
  },
  {
    component: () =>
      import(/* webpackChunkName: "support" */ "@/views/Support"),
    meta: { scrollToTop: true },
    path: "/support",
    name: "support",
  },
  {
    path: "*",
    redirect: "/",
  },
];

const scrollBehavior = (to, from, savedPosition) => {
  if (savedPosition) {
    return savedPosition;
  } else {
    let position = {};
    return new Promise((resolve) => {
      if (
        (to.name === "account" || !to.hash) &&
        to.matched.some((m) => m.meta.scrollToTop)
      ) {
        position.x = 0;
        position.y = 0;
      }
      resolve(position);
    });
  }
};

const router = new VueRouter({
  mode: "history",
  base: `${process.env.BASE_URL}${getStoreName()}`,
  scrollBehavior,
  routes: appRoutes,
});

router.beforeEach((to, from, next) => {
  store.state.notification.show && store.commit("notification/HIDE");
  navigationGuards.resetSearchResults(to, from);
  navigationGuards.checkIfLoyaltyIncluded(to, from, next);

  if (
    to.matched.some((record) => record.meta.requiresAuth) &&
    !store.getters["auth/isAuthenticated"]
  ) {
    const isMobile = vuetify.framework.breakpoint.xsOnly;
    if (isMobile) next("/login");
    else store.commit("auth/OPEN", true);

    const unsubscribe = store.subscribe((mutation) => {
      if (
        store.getters["auth/isAuthenticated"] &&
        mutation.type === "store/UPDATE"
      ) {
        next(to.fullPath);
        unsubscribe();
      }
    });
  } else next();
});

export default router;
